/**
 * 3D 导演台全屏工作台（P0-5b：三栏布局 + 机位系统改版）
 *
 * 布局（取证决策 1，对齐万镜一刻"面板为中心+视口为验证"范式）：
 *   左栏 outliner 汇总：角色/道具/机位三组列表（行内 可见/锁定/删除），
 *     添加区（角色模型/道具物体/机位：+记录视图、应用当前视角、预设两 tab）；
 *   中间视口：3D 场景 + 机位图标/视锥（CameraGizmos）+ 右上实时取景 PiP；
 *   右栏属性面板：选中机位→机位表单；选中对象→对象表单（角色含动作模板区）；
 *     都没选→场景（多维空间）表单。
 *
 * 交互规则（用户裁定，长期有效）：单选制；gizmo 常驻唯一；点空白不清除选中；
 *   双击视口机位 = 进入该机位视角（竞品没有的增强）；K = 快速建机位。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Box, Eye, EyeOff, Lock, Unlock, Video, Trash2 } from 'lucide-react';

import type { Object3D } from 'three';
import {
  TINT_PALETTE,
  tintAt,
  type BodyType,
  type CameraTarget,
  type DirectorObject,
  type GeometryType,
  type Transform,
  type Vec3,
} from '../../model/director';
import {
  type CharacterRig,
  type ControlName,
} from '../../model/pose';
import {
  restoreRest,
  type RestSnapshot,
} from '../../features/director/poseApply';
import { applyRigToModel } from '../../features/director/rigRender';
import {
  groundTransform,
  mirrorTransform,
} from '../../features/director/inspector';
import {
  CAMERA_PRESETS,
  findPreset,
  nextCameraName,
  resolvePresetPose,
} from '../../features/director/cameraPresets';
import { viewProbe, pipBadgeText } from '../../features/director/viewProbe';
import { NumberField } from '../../ui/NumberField';
import {
  DirectorViewport,
  type GizmoMode,
} from '../../features/director/DirectorViewport';
import { useCanvasStore } from '../../features/canvas/canvasStore';
import type { MediaRef } from '../../model/canvas';
import { isShotPayload } from '../../model/generation';
import { planCastSeed } from '../../features/director/castSeed';
import {
  BODY_TYPE_LABEL,
  GEOMETRY_LABEL,
  GEOMETRY_TYPES,
  isCharacterBody,
  nextObjectId,
} from '../../features/director/assets';
import { getRuntime, touchRuntime } from '../../features/director/runtimeRegistry';
import { type PoseEntry } from '../../features/director/poseCatalog';
import {
  boneGroups,
  clearControls,
  clearOverrides,
  CONTROL_GROUPS,
  controlValue,
  emptyRigState,
  withControl,
  withPreset,
} from '../../features/director/poseEdit';
import { PoseLibraryDialog } from '../../features/director/PoseLibraryDialog';
import { ContextMenu } from '../../features/director/ContextMenu';
import { menuFor, type MenuAction, type MenuTarget } from '../../features/director/menuRegistry';
import { STD_VIEWS, stdViewPose, viewCenter, type StdView } from '../../features/director/viewDirections';
import { viewCommand } from '../../features/director/viewCommand';
import {
  autosaveToServer,
  loadFromStorage,
  openProjectFile,
  saveProjectFile,
  saveToStorage,
} from '../../features/director/projectIO';
import { loadDirectorProject, parseAndLoad } from '../../model/persist';
import { useDirectorStore } from '../../features/director/store';
import { useSettingsStore } from '../settingsStore';
import { useAppCapability } from '../capabilityContext';

type CharacterBody = Exclude<BodyType, 'crowd' | 'geometry'>;
const CHARACTER_BODIES: readonly CharacterBody[] = ['standard', 'robust', 'broad', 'child'];

/** 动作模板：977 姿势库（分类+搜索+缩略图）走 PoseLibraryDialog 弹窗，见 P0-6b */

function makeCharacter(id: string, body: CharacterBody, order: number): DirectorObject {
  return {
    id,
    kind: 'character',
    name: `${BODY_TYPE_LABEL[body]}·${id}`,
    bodyType: body,
    transform: { position: [order * 1.2, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
    uniformScale: 1,
    scaleAxisLocked: true,
    visible: true,
    locked: false,
    tint: tintAt(order),
  };
}

function makeProp(id: string, geometry: GeometryType, order: number): DirectorObject {
  return {
    id,
    kind: 'prop',
    name: `${GEOMETRY_LABEL[geometry]}·${id}`,
    bodyType: 'geometry',
    geometryType: geometry,
    transform: { position: [order * 1.2, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
    uniformScale: 1,
    scaleAxisLocked: true,
    visible: true,
    locked: false,
    tint: tintAt(order),
  };
}

const chip = 'btn-ghost min-h-[28px] transition-colors duration-[120ms] ease-out';
const chipOn = 'border border-accent bg-surface-raised text-accent';
const iconBtn =
  'shrink-0 rounded-control p-1 text-ink-muted transition-colors duration-[120ms] ease-out hover:bg-surface-overlay hover:text-ink-primary';

function Group({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div className="mb-1">
      <p className="mb-0.5 flex items-center justify-between px-1 text-uiSm font-semibold text-ink-muted">
        {title}
        <span className="font-num">{count}</span>
      </p>
      <ul className="flex flex-col gap-0.5">{children}</ul>
    </div>
  );
}

/**
 * 从“关键帧节点 id”回溯它所属分镜的出场角色名：关键帧 ← 分镜节点（incoming 边）。
 * 读画布 store（与导演台跨页共享）。无上游分镜/非分镜→空数组。
 */
function shotCharactersForTarget(targetId: string): string[] {
  const doc = useCanvasStore.getState().doc;
  const fromId = doc.edges.find((e) => e.to === targetId)?.from;
  if (fromId === undefined) {
    return [];
  }
  const shotNode = doc.nodes.find((n) => n.id === fromId);
  if (shotNode === undefined || !isShotPayload(shotNode.payload)) {
    return [];
  }
  return shotNode.payload.shot.characters;
}

export function DirectorWorkbenchPage() {
  const { report, preset } = useAppCapability();
  const objects = useDirectorStore((s) => s.project.objects);
  const cameras = useDirectorStore((s) => s.project.cameras);
  const selectedIds = useDirectorStore((s) => s.selectedObjectIds);
  const selectedCameraId = useDirectorStore((s) => s.selectedCameraId);
  const activeCameraId = useDirectorStore((s) => s.project.activeCameraId);
  const viewMode = useDirectorStore((s) => s.viewMode);
  const selectedBoneName = useDirectorStore((s) => s.selectedBoneName);
  const canUndo = useDirectorStore((s) => s._past.length > 0);
  const canRedo = useDirectorStore((s) => s._future.length > 0);
  const scene = useDirectorStore((s) => s.project.scene);
  const [gizmoMode, setGizmoMode] = useState<GizmoMode>('translate');
  const [presetTab, setPresetTab] = useState<'scene' | 'character'>('scene');
  const [activePose, setActivePose] = useState<string | null>(null);
  const [activePoseLabel, setActivePoseLabel] = useState<string | null>(null);
  const [poseLibOpen, setPoseLibOpen] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; target: MenuTarget } | null>(null);
  const [activeView, setActiveView] = useState<StdView | null>(null);
  const [autosaveInfo, setAutosaveInfo] = useState<{
    time: string;
    path: string | null;
    viaServer: boolean;
  } | null>(null);
  const [status, setStatus] = useState('单击选择对象/机位；双击视口机位进入其视角；K 记录当前视图');
  const seededRef = useRef(false);
  const dirtyRef = useRef(false);
  const seededCastRef = useRef<string | null>(null);
  const autosaveIntervalSec = useSettingsStore((s) => s.autosaveIntervalSec);
  const setAutosaveInterval = useSettingsStore((s) => s.setAutosaveInterval);

  // 画布关键帧→导演台往返（刀 4b）：若带着 directorTarget 进来，构图完可导出视口回写该关键帧
  const navigate = useNavigate();
  const directorTarget = useCanvasStore((s) => s.directorTarget);
  const captureRef = useRef<null | (() => string | null)>(null);
  const backToCanvas = useCallback(() => {
    useCanvasStore.getState().setDirectorTarget(null);
    navigate('/canvas/editor');
  }, [navigate]);
  const exportKeyframe = useCallback(async () => {
    if (directorTarget === null) {
      return;
    }
    // 导出态隐藏叠加层→等两帧让视口重绘干净→截图→恢复
    useDirectorStore.getState().setCapturing(true);
    await new Promise<void>((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => r())),
    );
    const url = captureRef.current?.();
    useDirectorStore.getState().setCapturing(false);
    if (url === null || url === undefined) {
      setStatus('无法导出视图，稍候重试');
      return;
    }
    const media: MediaRef = { mediaId: `director-${directorTarget}-${Date.now().toString(36)}`, url, thumbnail: url };
    useCanvasStore.getState().updateNode(directorTarget, { media });
    useCanvasStore.getState().setDirectorTarget(null);
    setStatus('已导出为分镜关键帧');
    navigate('/canvas/editor');
  }, [directorTarget, navigate]);

  const onError = useCallback((message: string) => setStatus(message), []);
  const onPickDebug = useCallback((message: string) => setStatus(message), []);

  // 首次进入：为关键帧构图则重置为空工程；否则优先恢复上次自动存档，无存档才放一个默认角色
  useEffect(() => {
    if (seededRef.current) {
      return;
    }
    seededRef.current = true;
    const s = useDirectorStore.getState();
    // 关键帧构图态（带 directorTarget）：重置为干净空工程，不复用上次自动存档/示例，
    // 交给下方 cast seed 带入本镜角色（避免“打开却看到的是旧场景”）
    if (useCanvasStore.getState().directorTarget !== null) {
      s.resetProject();
      return;
    }
    if (s.project.objects.length > 0) {
      return; // 已有内容（如外部载入），不覆盖
    }
    const restored = loadFromStorage();
    if (restored !== null && restored.ok) {
      s.setProject(restored.doc);
      setStatus('已恢复上次自动保存的工程');
      return;
    }
    if (restored !== null && !restored.ok) {
      setStatus(`上次自动存档无法恢复（${restored.reason}），已新建空白工程`);
    }
    const id = nextObjectId([]);
    s.addObject(makeCharacter(id, 'standard', 0));
    s.selectObjects([id]);
  }, []);

  // directormapping：从关键帧进导演台时，把该镜出场角色“带入”——按名额生成对应素体并命名
  useEffect(() => {
    if (directorTarget === null) {
      seededCastRef.current = null;
      return;
    }
    if (seededCastRef.current === directorTarget) {
      return;
    }
    seededCastRef.current = directorTarget;
    const chars = shotCharactersForTarget(directorTarget);
    if (chars.length === 0) {
      return;
    }
    const cur = useDirectorStore.getState();
    const plan = planCastSeed(chars, cur.project.objects.map((o) => o.name));
    if (plan.length === 0) {
      return;
    }
    const ids = cur.project.objects.map((o) => o.id);
    let order = cur.project.objects.length;
    const newIds: string[] = [];
    for (const p of plan) {
      const id = nextObjectId(ids);
      ids.push(id);
      newIds.push(id);
      useDirectorStore.getState().addObject({ ...makeCharacter(id, p.body, order), name: p.name });
      order += 1;
    }
    if (newIds[0] !== undefined) {
      useDirectorStore.getState().selectObjects([newIds[0]]);
    }
    setStatus(`已带入本镜角色：${plan.map((p) => p.name).join('、')}`);
  }, [directorTarget]);

  // 自动保存（P0-8）：按“设置”的间隔落盘——localStorage（快速恢复）+ 服务器真实文件（防意外退出）。
  // 脏标记 + 定时 flush；页面隐藏/卸载时补 flush，尽量不丢最后一个间隔内的改动。
  const saveNow = useCallback(() => {
    if (!dirtyRef.current) {
      return;
    }
    dirtyRef.current = false;
    const proj = useDirectorStore.getState().project;
    saveToStorage(proj);
    void autosaveToServer(proj).then((meta) => {
      setAutosaveInfo({
        time: new Date().toLocaleTimeString('zh-CN', { hour12: false }),
        path: meta?.path ?? null,
        viaServer: meta !== null,
      });
    });
  }, []);

  // 任何可序列化部分变化 → 标脏
  useEffect(() => {
    dirtyRef.current = true;
  }, [objects, cameras, scene, activeCameraId]);

  // 定时器：按设置间隔落盘；离开页/切后台时补存
  useEffect(() => {
    const id = setInterval(saveNow, autosaveIntervalSec * 1000);
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') {
        saveNow();
      }
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('beforeunload', saveNow);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('beforeunload', saveNow);
      saveNow();
    };
  }, [autosaveIntervalSec, saveNow]);

  /* ---------- 工程存/开（文件） ---------- */
  async function handleSaveFile(): Promise<void> {
    const outcome = await saveProjectFile(useDirectorStore.getState().project);
    setStatus(
      outcome === 'saved' ? '已保存工程文件' : outcome === 'cancelled' ? '已取消保存' : '保存工程失败',
    );
  }

  async function handleOpenFile(): Promise<void> {
    const result = await openProjectFile();
    if (!result.ok) {
      setStatus(result.reason === 'cancelled' ? '已取消打开' : '打开失败');
      return;
    }
    const loaded = parseAndLoad(result.text, loadDirectorProject);
    if (!loaded.ok) {
      setStatus(`无法打开：${loaded.message}`);
      return;
    }
    const ok = useDirectorStore.getState().setProject(loaded.doc);
    setStatus(ok ? '已载入工程文件' : '载入被拒绝（结构不合法）');
  }

  /* ---------- 对象 ---------- */
  const selObj =
    selectedIds.length === 1 && selectedCameraId === null
      ? objects.find((o) => o.id === selectedIds[0]) ?? null
      : null;

  function addCharacter(body: CharacterBody): void {
    const s = useDirectorStore.getState();
    const id = nextObjectId(s.project.objects.map((o) => o.id));
    s.addObject(makeCharacter(id, body, s.project.objects.length));
    s.selectObjects([id]);
    s.selectCamera(null);
    setStatus(`已添加 ${BODY_TYPE_LABEL[body]}（${id}）`);
  }

  function addGeometry(g: GeometryType): void {
    const s = useDirectorStore.getState();
    const id = nextObjectId(s.project.objects.map((o) => o.id));
    s.addObject(makeProp(id, g, s.project.objects.length));
    s.selectObjects([id]);
    s.selectCamera(null);
    setStatus(`已添加 ${GEOMETRY_LABEL[g]}（${id}）`);
  }

  function removeSelectedObject(id: string): void {
    useDirectorStore.getState().removeObject(id);
    setActivePose(null);
    setStatus(`已删除 ${id}`);
  }

  const patchSel = (
    patch: Partial<Omit<DirectorObject, 'id' | 'transform'>> & { transform?: Partial<Transform> },
  ): void => {
    if (selObj !== null) {
      useDirectorStore.getState().updateObject(selObj.id, patch);
    }
  };
  const patchSelTransform = (t: Transform): void => patchSel({ transform: t });

  /* ---------- 姿势与关节（作用于选中角色，数据驱动：rig 存 store，合成后施加） ---------- */
  function poseTarget():
    | { id: string; root: Object3D; rest: RestSnapshot; rig: CharacterRig | undefined }
    | null {
    const s = useDirectorStore.getState();
    for (const id of s.selectedObjectIds) {
      const entry = getRuntime(id);
      if (entry !== null && entry.root !== null) {
        const obj = s.project.objects.find((o) => o.id === id);
        return { id, root: entry.root, rest: entry.rest, rig: obj?.rig };
      }
    }
    return null;
  }

  /** 由 rig 合成 65 骨并施加：预设（无则全骨零位）⊕ controls ⊕ overrides */
  async function renderRig(
    root: Object3D,
    rest: RestSnapshot,
    rig: CharacterRig,
  ): Promise<number> {
    const applied = await applyRigToModel(root, rest, rig);
    touchRuntime();
    return applied;
  }

  /** 撤销/重做后把每个角色的视觉姿态重新对齐到 store 里的 rig（修复：姿势回退只改数据不回摆模型）。
   *  位置/机位由 transform effect 自动回退，但骨骼姿态是命令式施加的，需显式重刷。 */
  function rerenderAllRigs(): void {
    const s = useDirectorStore.getState();
    for (const o of s.project.objects) {
      const entry = getRuntime(o.id);
      if (entry !== null && entry.root !== null) {
        void applyRigToModel(entry.root, entry.rest, o.rig ?? emptyRigState());
      }
    }
    touchRuntime();
  }

  async function applyPose(pose: PoseEntry): Promise<void> {
    const target = poseTarget();
    if (target === null) {
      setStatus('请先选中一个角色（几何体没有骨骼）');
      return;
    }
    // 换预设 = 回到该姿势原样，清掉上一套关节微调（竞品也是这个语义）
    const nextRig = withPreset(clearControls(target.rig ?? emptyRigState()), pose.id);
    useDirectorStore.getState().updateObject(target.id, { rig: nextRig });
    try {
      const applied = await renderRig(target.root, target.rest, nextRig);
      setActivePose(pose.id);
      setActivePoseLabel(pose.name);
      setStatus(`已套用「${pose.name}」(${pose.id})：${applied} 骨`);
    } catch (e) {
      setStatus(`套用姿势异常：${String(e)}`);
    }
  }

  /** 拖关节滑杆：写入 rig.controls 并实时重合成施加 */
  function onControlChange(name: ControlName, deg: number): void {
    const target = poseTarget();
    if (target === null) {
      return;
    }
    const next = withControl(target.rig ?? emptyRigState(), name, deg);
    useDirectorStore.getState().updateObject(target.id, { rig: next });
    void renderRig(target.root, target.rest, next);
  }

  /** 重置全部关节微调（保留当前预设姿势） */
  function resetControls(): void {
    const target = poseTarget();
    if (target === null || target.rig === undefined) {
      return;
    }
    const next = clearControls(target.rig);
    useDirectorStore.getState().updateObject(target.id, { rig: next });
    void renderRig(target.root, target.rest, next);
    setStatus('已重置关节微调');
  }

  /** 清除全部单骨覆盖（逐骨 gizmo 的撤销路径），回落到预设+滑杆层 */
  function clearAllOverrides(): void {
    const target = poseTarget();
    if (target === null || target.rig === undefined) {
      return;
    }
    const next = clearOverrides(target.rig);
    useDirectorStore.getState().updateObject(target.id, { rig: next });
    void renderRig(target.root, target.rest, next);
    setStatus('已清除单骨覆盖');
  }

  function toRest(): void {
    const target = poseTarget();
    if (target === null) {
      setStatus('请先选中一个角色');
      return;
    }
    const n = restoreRest(target.rest);
    useDirectorStore.getState().updateObject(target.id, { rig: emptyRigState() });
    touchRuntime();
    setActivePose(null);
    setActivePoseLabel(null);
    setStatus(`已还原 rest（${n} 根蒙皮骨骼）`);
  }

  /* ---------- 机位 ---------- */
  const selCam = cameras.find((c) => c.id === selectedCameraId) ?? null;

  function recordView(): void {
    const v = viewProbe.get();
    if (v === null) {
      setStatus('视口尚未就绪，无法记录视图');
      return;
    }
    const s = useDirectorStore.getState();
    const id = nextObjectId(s.project.cameras.map((c) => c.id), 'cam');
    const name = nextCameraName(s.project.cameras.map((c) => c.name), '机位');
    const ok = s.addCamera({
      id,
      name,
      position: [v.px, v.py, v.pz],
      fov: v.fov,
      target: { mode: 'point', point: [v.tx, v.ty, v.tz], followId: null },
    });
    if (ok) {
      s.selectCamera(id);
      s.setActiveCamera(id);
      setStatus(`已把当前视图记录为「${name}」并设为取景`);
    }
  }

  /** 选中机位时把当前自由视角灌给它（取证借鉴④：上下文"应用当前视角"按钮） */
  function applyViewToSelectedCamera(): void {
    const v = viewProbe.get();
    if (v === null || selCam === null) {
      return;
    }
    useDirectorStore.getState().updateCamera(selCam.id, {
      position: [v.px, v.py, v.pz],
      fov: v.fov,
      target: { mode: 'point', point: [v.tx, v.ty, v.tz], followId: null },
    });
    setStatus(`已把当前视图应用到「${selCam.name}」`);
  }

  function applyPreset(presetId: string): void {
    const p = findPreset(presetId);
    if (p === null) {
      return;
    }
    const s = useDirectorStore.getState();
    const chars = s.project.objects.filter((o) => isCharacterBody(o.bodyType));
    const targetChar = chars.find((o) => s.selectedObjectIds.includes(o.id)) ?? chars[0];
    if (presetTab === 'character' && targetChar === undefined) {
      setStatus('「角色」预设需要先有一个角色（相对角色朝向解算）');
      return;
    }
    const feet: Vec3 = targetChar?.transform.position ?? [0, 0, 0];
    const charYaw = presetTab === 'character' && targetChar !== undefined ? targetChar.transform.rotation[1] : 0;
    const pose = resolvePresetPose(feet, p, charYaw);
    const target: CameraTarget =
      presetTab === 'character' && targetChar !== undefined
        ? { mode: 'follow', point: [0, 0, 0], followId: targetChar.id }
        : { mode: 'point', point: pose.target, followId: null };
    let camId = s.selectedCameraId;
    if (camId === null || !s.project.cameras.some((c) => c.id === camId)) {
      camId = nextObjectId(s.project.cameras.map((c) => c.id), 'cam');
      s.addCamera({
        id: camId,
        name: nextCameraName(s.project.cameras.map((c) => c.name), p.label),
        position: pose.position,
        fov: p.fov,
        target,
      });
    } else {
      s.updateCamera(camId, { position: pose.position, fov: p.fov, target });
    }
    s.setActiveCamera(camId);
    s.selectCamera(camId);
    setStatus(`预设「${p.label}」(${presetTab === 'character' ? '角色相对' : '世界方位'}) 已应用并取景`);
  }

  const patchCam = (
    patch: { name?: string; position?: Transform['position']; fov?: number; target?: CameraTarget },
  ): void => {
    if (selCam !== null) {
      useDirectorStore.getState().updateCamera(selCam.id, patch);
    }
  };

  function enterCameraView(camId: string): void {
    const s = useDirectorStore.getState();
    s.selectCamera(camId);
    s.selectObjects([]);
    s.setActiveCamera(camId);
    s.setViewMode('camera');
  }

  /** 六向标准视图（B-1）：以选中角色（否则全体质心）为注视点，从指定正交方向看过去 */
  function applyStdView(dir: StdView): void {
    const s = useDirectorStore.getState();
    const selectedId = s.selectedObjectIds.length === 1 ? s.selectedObjectIds[0] ?? null : null;
    const center = viewCenter(s.project.objects, selectedId);
    const pose = stdViewPose(center, 6, dir);
    s.setViewMode('free');
    viewCommand.apply(pose);
    setActiveView(dir);
    setStatus(`已切到${STD_VIEWS.find((v) => v.id === dir)?.label ?? ''}视图`);
  }

  /* ---------- 右键上下文菜单（B-2）：目标由当前选中态推导，行为由注册表 action 派发 ---------- */
  function currentMenuTarget(): MenuTarget {
    const s = useDirectorStore.getState();
    if (s.selectedBoneName !== null) {
      return 'bone';
    }
    if (s.selectedCameraId !== null) {
      return 'camera';
    }
    if (s.selectedObjectIds.length === 1) {
      const o = s.project.objects.find((x) => x.id === s.selectedObjectIds[0]);
      if (o !== undefined) {
        return isCharacterBody(o.bodyType) ? 'character' : 'prop';
      }
    }
    return 'empty';
  }

  function openContextMenu(e: { preventDefault(): void; clientX: number; clientY: number }): void {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, target: currentMenuTarget() });
  }

  function duplicateSelected(): void {
    const s = useDirectorStore.getState();
    const src = s.project.objects.find((o) => o.id === s.selectedObjectIds[0]);
    if (src === undefined) {
      return;
    }
    const id = nextObjectId(s.project.objects.map((o) => o.id));
    s.addObject({
      ...src,
      id,
      name: `${src.name}·副本`,
      transform: {
        ...src.transform,
        position: [src.transform.position[0] + 1.2, src.transform.position[1], src.transform.position[2]],
      },
    });
    s.selectObjects([id]);
    setStatus(`已复制为 ${id}`);
  }

  function resetSelTransform(): void {
    if (selObj === null) {
      return;
    }
    useDirectorStore.getState().updateObject(selObj.id, {
      transform: { position: selObj.transform.position, rotation: [0, 0, 0], scale: [1, 1, 1] },
      uniformScale: 1,
    });
    setStatus('已重置变换');
  }

  function clearSelectedBoneOverride(): void {
    const s = useDirectorStore.getState();
    const bone = s.selectedBoneName;
    const id = s.selectedObjectIds[0];
    if (bone === null || id === undefined) {
      return;
    }
    const obj = s.project.objects.find((o) => o.id === id);
    if (obj?.rig === undefined) {
      return;
    }
    const boneOverrides = { ...obj.rig.boneOverrides };
    delete boneOverrides[bone];
    const next: CharacterRig = { ...obj.rig, boneOverrides };
    s.updateObject(id, { rig: next });
    const entry = getRuntime(id);
    if (entry !== null && entry.root !== null) {
      void renderRig(entry.root, entry.rest, next);
    }
    setStatus(`已清除 ${bone} 的覆盖`);
  }

  function dispatchMenuAction(action: MenuAction): void {
    const s = useDirectorStore.getState();
    switch (action) {
      case 'duplicate':
        duplicateSelected();
        break;
      case 'delete':
        if (s.selectedCameraId !== null) {
          s.removeCamera(s.selectedCameraId);
          setStatus('已删除机位');
        } else if (s.selectedObjectIds[0] !== undefined) {
          removeSelectedObject(s.selectedObjectIds[0]);
        }
        break;
      case 'resetTransform':
        resetSelTransform();
        break;
      case 'clearPose':
        toRest();
        break;
      case 'clearBoneOverride':
        clearSelectedBoneOverride();
        break;
      case 'enterCameraView':
        if (s.selectedCameraId !== null) {
          enterCameraView(s.selectedCameraId);
        }
        break;
      case 'setActiveCamera':
        if (s.selectedCameraId !== null) {
          s.setActiveCamera(s.selectedCameraId);
          setStatus('已设为取景机位');
        }
        break;
      case 'resetView':
        s.setViewMode('free');
        viewCommand.apply({ position: [2.6, 1.8, 3.2], target: [0, 1, 0] });
        setStatus('已重置视角');
        break;
      case 'deselect':
        s.selectObjects([]);
        s.selectCamera(null);
        break;
    }
  }

  /* ---------- 快捷键 ---------- */
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const t = e.target as HTMLElement | null;
      if (t !== null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      const s = useDirectorStore.getState();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) {
          s.redo();
        } else {
          s.undo();
        }
        rerenderAllRigs();
        setActivePose(null);
      } else if (mod && (e.key === 'y' || e.key === 'Y')) {
        e.preventDefault();
        s.redo();
        rerenderAllRigs();
        setActivePose(null);
      } else if (e.key === 'v' || e.key === 'V') {
        setGizmoMode('translate');
      } else if (e.key === 'r' || e.key === 'R') {
        setGizmoMode('rotate');
      } else if (e.key === 'f' || e.key === 'F') {
        setGizmoMode('scale');
      } else if (e.key === 'k' || e.key === 'K') {
        recordView();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        for (const id of s.selectedObjectIds) {
          s.removeObject(id);
        }
        setActivePose(null);
      } else if (e.key === 'Escape') {
        s.selectObjects([]);
        s.selectCamera(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (preset === null || report === null) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-uiSm text-ink-muted">正在检测本机图形能力…</p>
      </div>
    );
  }

  const modeBtn = (m: GizmoMode): string => `${chip} ${gizmoMode === m ? chipOn : ''}`;
  const characters = objects.filter((o) => isCharacterBody(o.bodyType));
  const props = objects.filter((o) => !isCharacterBody(o.bodyType));
  const badge = pipBadgeText(selCam, viewMode);
  const selectedCharId = selectedIds.length === 1 ? selectedIds[0] : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1">
        {/* ===== 左栏：outliner 汇总 + 添加区 ===== */}
        <aside className="flex w-60 shrink-0 flex-col overflow-y-auto border-r border-surface-line bg-surface-overlay">
          <div className="flex flex-wrap items-center gap-1.5 border-b border-surface-line px-3 py-2">
            <button type="button" className={chip} onClick={() => void handleSaveFile()}>
              保存工程
            </button>
            <button type="button" className={chip} onClick={() => void handleOpenFile()}>
              打开工程
            </button>
            <button
              type="button"
              className={`${chip} disabled:cursor-not-allowed disabled:opacity-40`}
              onClick={() => {
                useDirectorStore.getState().undo();
                rerenderAllRigs();
                setActivePose(null);
              }}
              disabled={!canUndo}
              title="撤销 (Ctrl+Z)"
            >
              撤销
            </button>
            <button
              type="button"
              className={`${chip} disabled:cursor-not-allowed disabled:opacity-40`}
              onClick={() => {
                useDirectorStore.getState().redo();
                rerenderAllRigs();
                setActivePose(null);
              }}
              disabled={!canRedo}
              title="重做 (Ctrl+Shift+Z / Ctrl+Y)"
            >
              重做
            </button>
            <label className="flex items-center gap-1 text-uiSm text-ink-muted">
              自动保存
              <select
                value={String(autosaveIntervalSec)}
                onChange={(e) => setAutosaveInterval(Number(e.target.value))}
                aria-label="自动保存间隔"
                className="rounded-control border border-surface-edge bg-surface-base px-1 py-0.5 text-uiSm text-ink-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
              >
                <option value="10">每 10 秒</option>
                <option value="30">每 30 秒</option>
                <option value="60">每 1 分钟</option>
                <option value="300">每 5 分钟</option>
              </select>
            </label>
            <span
              className="w-full text-uiSm text-ink-muted"
              title={autosaveInfo?.path ?? '自动保存到浏览器本地存储（localStorage）与服务器真实文件'}
            >
              {autosaveInfo
                ? `已存 ${autosaveInfo.time} · ${autosaveInfo.viaServer ? '文件' : '本地'}`
                : `每 ${autosaveIntervalSec} 秒自动保存`}
            </span>
          </div>
          <div className="border-b border-surface-line px-3 py-2.5">
            <Group title="角色" count={characters.length}>
              {characters.map((o) => (
                <li key={o.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-pressed={selectedIds.includes(o.id)}
                    onClick={() => {
                      const s = useDirectorStore.getState();
                      s.selectObjects([o.id]);
                      s.selectCamera(null);
                    }}
                    className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-control px-1.5 py-1 text-left text-uiSm transition-colors duration-[120ms] ease-out ${
                      selectedIds.includes(o.id)
                        ? 'border-l-2 border-l-accent bg-surface-raised text-accent'
                        : 'text-ink-secondary hover:bg-surface-raised hover:text-ink-primary'
                    }`}
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: o.tint }} />
                    <span className="truncate">{o.name}</span>
                  </button>
                  <button type="button" className={iconBtn} aria-label="可见性"
                    onClick={() => useDirectorStore.getState().updateObject(o.id, { visible: !o.visible })}>
                    {o.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                  <button type="button" className={iconBtn} aria-label="锁定"
                    onClick={() => useDirectorStore.getState().updateObject(o.id, { locked: !o.locked })}>
                    {o.locked ? <Lock size={13} /> : <Unlock size={13} />}
                  </button>
                  <button type="button" className={iconBtn} aria-label="删除" onClick={() => removeSelectedObject(o.id)}>
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </Group>
            <Group title="道具" count={props.length}>
              {props.map((o) => (
                <li key={o.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-pressed={selectedIds.includes(o.id)}
                    onClick={() => {
                      const s = useDirectorStore.getState();
                      s.selectObjects([o.id]);
                      s.selectCamera(null);
                    }}
                    className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-control px-1.5 py-1 text-left text-uiSm transition-colors duration-[120ms] ease-out ${
                      selectedIds.includes(o.id)
                        ? 'border-l-2 border-l-accent bg-surface-raised text-accent'
                        : 'text-ink-secondary hover:bg-surface-raised hover:text-ink-primary'
                    }`}
                  >
                    <Box size={13} className="shrink-0" />
                    <span className="truncate">{o.name}</span>
                  </button>
                  <button type="button" className={iconBtn} aria-label="可见性"
                    onClick={() => useDirectorStore.getState().updateObject(o.id, { visible: !o.visible })}>
                    {o.visible ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                  <button type="button" className={iconBtn} aria-label="删除" onClick={() => removeSelectedObject(o.id)}>
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </Group>
            <Group title="机位" count={cameras.length}>
              {cameras.map((c) => {
                const followsSelected =
                  selectedCharId !== null && c.target.mode === 'follow' && c.target.followId === selectedCharId;
                return (
                  <li key={c.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-pressed={selectedCameraId === c.id}
                      onClick={() => {
                        const s = useDirectorStore.getState();
                        s.selectCamera(c.id);
                        s.selectObjects([]);
                      }}
                      className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-control px-1.5 py-1 text-left text-uiSm transition-colors duration-[120ms] ease-out ${
                        selectedCameraId === c.id
                          ? 'border-l-2 border-l-accent bg-surface-raised text-accent'
                          : 'text-ink-secondary hover:bg-surface-raised hover:text-ink-primary'
                      }`}
                    >
                      <Video size={13} className="shrink-0" />
                      <span className="truncate">{c.name}</span>
                      {c.id === activeCameraId && <span className="shrink-0 text-uiSm text-accent">取景</span>}
                      {followsSelected && <span className="shrink-0 text-uiSm text-state-info">跟随选中</span>}
                    </button>
                    {followsSelected && (
                      <button type="button" className={iconBtn} aria-label="跳到该机位" onClick={() => enterCameraView(c.id)}>
                        <Video size={13} />
                      </button>
                    )}
                    <button type="button" className={iconBtn} aria-label="删除机位"
                      onClick={() => useDirectorStore.getState().removeCamera(c.id)}>
                      <Trash2 size={13} />
                    </button>
                  </li>
                );
              })}
            </Group>
          </div>

          {/* 添加区 */}
          <div className="border-b border-surface-line px-3 py-2.5">
            <p className="mb-1 text-uiSm font-semibold text-ink-muted">角色模型</p>
            <div className="flex flex-wrap gap-1.5">
              {CHARACTER_BODIES.map((b) => (
                <button key={b} type="button" className={chip} onClick={() => addCharacter(b)}>
                  +{BODY_TYPE_LABEL[b]}
                </button>
              ))}
            </div>
            <p className="mb-1 mt-2 text-uiSm font-semibold text-ink-muted">道具物体</p>
            <div className="flex flex-wrap gap-1.5">
              {GEOMETRY_TYPES.map((g) => (
                <button key={g} type="button" className={chip} onClick={() => addGeometry(g)}>
                  +{GEOMETRY_LABEL[g]}
                </button>
              ))}
            </div>
            <p className="mb-1 mt-2 text-uiSm font-semibold text-ink-muted">机位</p>
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={chip} onClick={recordView}>+ 记录当前视图 (K)</button>
              {selCam !== null && (
                <button type="button" className={chip} onClick={applyViewToSelectedCamera}>应用当前视角</button>
              )}
            </div>
            <details className="mt-1.5">
              <summary className="cursor-pointer select-none text-uiSm font-semibold text-ink-muted hover:text-ink-primary">
                机位预设（{presetTab === 'scene' ? '场景' : '角色'}）
              </summary>
              <div className="mb-1 mt-1 flex gap-1.5">
                <button type="button" className={`${chip} px-2 text-uiSm ${presetTab === 'scene' ? chipOn : ''}`}
                  onClick={() => setPresetTab('scene')}>场景</button>
                <button type="button" className={`${chip} px-2 text-uiSm ${presetTab === 'character' ? chipOn : ''}`}
                  onClick={() => setPresetTab('character')}>角色</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {CAMERA_PRESETS.map((p) => (
                  <button key={p.id} type="button" className={`${chip} px-2 text-uiSm`} onClick={() => applyPreset(p.id)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </details>
          </div>

          <p className="break-words px-3 py-2 text-uiSm text-ink-muted">{status}</p>
        </aside>

        {/* ===== 中：视口 ===== */}
        <main className="relative min-h-0 min-w-0 flex-1" onContextMenu={openContextMenu}>
          <DirectorViewport preset={preset} gizmoMode={gizmoMode} onError={onError} onPickDebug={onPickDebug} captureRef={captureRef} />
            {/* 关键帧构图态（刀 4b）：带 directorTarget 进来时，给一个导出视图回写的出口 */}
            {directorTarget !== null && (
              <div className="absolute left-3 top-3 z-20 flex items-center gap-2 rounded-card border border-accent/60 bg-surface-overlay px-2.5 py-1.5 shadow-[0_6px_20px_rgba(0,0,0,.5)]">
                <span className="text-uiSm text-ink-secondary">正在为分镜关键帧构图</span>
                <button
                  type="button"
                  onClick={() => void exportKeyframe()}
                  className="rounded-control bg-accent px-2.5 py-1 text-uiSm font-semibold text-accent-ink transition-opacity hover:opacity-90"
                >
                  导出为关键帧
                </button>
                <button type="button" onClick={backToCanvas} className="text-uiSm text-ink-muted transition-colors hover:text-ink-primary">
                  不导出返回
                </button>
              </div>
            )}
          {/* 顶中：视角模式切换（取证 Q8） */}
          <div className="pointer-events-none absolute left-1/2 top-2 flex -translate-x-1/2 items-center gap-1 rounded-card border border-surface-line bg-surface-overlay px-1.5 py-1">
            <button
              type="button"
              className={`${chip} pointer-events-auto px-2 text-uiSm ${viewMode === 'free' ? chipOn : ''}`}
              onClick={() => useDirectorStore.getState().setViewMode('free')}
            >
              自由视角
            </button>
            <button
              type="button"
              className={`${chip} pointer-events-auto px-2 text-uiSm ${viewMode === 'camera' ? chipOn : ''}`}
              onClick={() => {
                const s = useDirectorStore.getState();
                const id = s.selectedCameraId ?? s.project.activeCameraId;
                if (id === null) {
                  setStatus('还没有机位，先按 K 或左栏记录一个');
                  return;
                }
                s.setActiveCamera(id);
                s.setViewMode('camera');
              }}
            >
              相机视角
            </button>
          </div>
          {/* 右上：PiP 角标（画布内 scissor 与此对齐） */}
          {badge !== null && (
            <div className="pointer-events-none absolute right-3 top-3 rounded-control border border-surface-line bg-surface-overlay px-2 py-0.5 text-uiSm text-ink-secondary">
              {badge}
            </div>
          )}
          {/* 底部居中：六向标准视图（B-1）——选中变色 + 卡片边缘发光 */}
          <div className="glow-card absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-card border border-surface-edge bg-surface-overlay px-2 py-1.5">
            <span className="px-1 text-uiSm text-ink-muted">视图</span>
            {STD_VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                aria-pressed={activeView === v.id}
                className={`${chip} px-2 text-uiSm ${activeView === v.id ? chipOn : ''}`}
                title={`${v.label}视图`}
                onClick={() => applyStdView(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
          {menu !== null && (
            <ContextMenu
              x={menu.x}
              y={menu.y}
              items={menuFor(menu.target)}
              onSelect={(action) => {
                setMenu(null);
                dispatchMenuAction(action);
              }}
              onClose={() => setMenu(null)}
            />
          )}
        </main>

        {/* ===== 右栏：属性面板（选中机位 > 选中对象 > 场景） ===== */}
        <aside className="w-72 shrink-0 overflow-y-auto border-l border-surface-line bg-surface-overlay">
          {selCam !== null ? (
            <div className="px-3 py-2.5">
              <p className="mb-2 text-ui font-semibold">摄像机 · {selCam.id}</p>
              <p className="mb-2 text-uiSm leading-snug text-ink-muted">
                视口内：按 V 拖机身移位置、按 R 拖蓝色注视球改角度（拖注视球会解绑跟随）
              </p>
              <input type="text" value={selCam.name} onChange={(e) => patchCam({ name: e.target.value })}
                className="mb-2 w-full rounded-control border border-surface-line bg-surface-base px-2 py-1 text-uiSm text-ink-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                aria-label="机位名称" />
              <label className="mb-2 flex items-center gap-2 text-uiSm text-ink-muted">
                视野
                <input type="range" min={10} max={120} step={1} value={selCam.fov}
                  onChange={(e) => patchCam({ fov: Number(e.target.value) })}
                  className="slider min-w-0 flex-1" />
                <span className="w-8 shrink-0 text-right font-num">{selCam.fov.toFixed(0)}</span>
              </label>
              <p className="mb-1 text-uiSm font-semibold text-ink-muted">位置</p>
              <div className="mb-2 flex gap-1">
                <NumberField label="X" value={selCam.position[0]} onCommit={(v) => patchCam({ position: [v, selCam.position[1], selCam.position[2]] })} />
                <NumberField label="Y" value={selCam.position[1]} onCommit={(v) => patchCam({ position: [selCam.position[0], v, selCam.position[2]] })} />
                <NumberField label="Z" value={selCam.position[2]} onCommit={(v) => patchCam({ position: [selCam.position[0], selCam.position[1], v] })} />
              </div>
              <p className="mb-1 text-uiSm font-semibold text-ink-muted">注视目标</p>
              <div className="mb-1 flex items-center gap-3 text-uiSm text-ink-secondary">
                <label className="flex items-center gap-1">
                  <input type="radio" name="camTargetMode" checked={selCam.target.mode === 'point'}
                    onChange={() => patchCam({ target: { mode: 'point', point: selCam.target.mode === 'point' ? selCam.target.point : [0, 1, 0], followId: null } })} />
                  点位
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="camTargetMode" checked={selCam.target.mode === 'follow'}
                    onChange={() => {
                      const first = characters[0];
                      if (first !== undefined) {
                        patchCam({ target: { mode: 'follow', point: [0, 0, 0], followId: first.id } });
                      } else {
                        setStatus('场景里没有可跟随的角色');
                      }
                    }} />
                  跟随角色
                </label>
              </div>
              {selCam.target.mode === 'follow' && (
                <div className="mb-2 flex flex-wrap gap-1">
                  {characters.map((o) => (
                    <button key={o.id} type="button"
                      aria-pressed={selCam.target.mode === 'follow' && selCam.target.followId === o.id}
                      className={`${chip} px-2 text-uiSm ${selCam.target.mode === 'follow' && selCam.target.followId === o.id ? chipOn : ''}`}
                      onClick={() => patchCam({ target: { mode: 'follow', point: [0, 0, 0], followId: o.id } })}>
                      {o.name}
                    </button>
                  ))}
                </div>
              )}
              {selCam.target.mode === 'point' && (
                <div className="mb-2 flex gap-1">
                  <NumberField label="X" value={selCam.target.point[0]} onCommit={(v) => patchCam({ target: { mode: 'point', point: [v, selCam.target.point[1], selCam.target.point[2]], followId: null } })} />
                  <NumberField label="Y" value={selCam.target.point[1]} onCommit={(v) => patchCam({ target: { mode: 'point', point: [selCam.target.point[0], v, selCam.target.point[2]], followId: null } })} />
                  <NumberField label="Z" value={selCam.target.point[2]} onCommit={(v) => patchCam({ target: { mode: 'point', point: [selCam.target.point[0], selCam.target.point[1], v], followId: null } })} />
                </div>
              )}
              <button type="button" className={`${chip} w-full`} onClick={() => enterCameraView(selCam.id)}>
                进入该机位视角
              </button>
            </div>
          ) : selObj !== null ? (
            <div className="px-3 py-2.5">
              <p className="mb-2 text-ui font-semibold">对象 · {selObj.id}</p>
              <input type="text" value={selObj.name} onChange={(e) => patchSel({ name: e.target.value })}
                className="mb-2 w-full rounded-control border border-surface-line bg-surface-base px-2 py-1 text-uiSm text-ink-primary focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                aria-label="对象名称" />
              <p className="mb-1 text-uiSm font-semibold text-ink-muted">位置</p>
              <div className="mb-2 flex gap-1">
                <NumberField label="X" value={selObj.transform.position[0]} onCommit={(v) => patchSelTransform({ ...selObj.transform, position: [v, selObj.transform.position[1], selObj.transform.position[2]] })} />
                <NumberField label="Y" value={selObj.transform.position[1]} onCommit={(v) => patchSelTransform({ ...selObj.transform, position: [selObj.transform.position[0], v, selObj.transform.position[2]] })} />
                <NumberField label="Z" value={selObj.transform.position[2]} onCommit={(v) => patchSelTransform({ ...selObj.transform, position: [selObj.transform.position[0], selObj.transform.position[1], v] })} />
              </div>
              <p className="mb-1 text-uiSm font-semibold text-ink-muted">旋转（度）</p>
              <div className="mb-2 flex gap-1">
                <NumberField label="转" value={selObj.transform.rotation[0]} min={-180} max={180} step={5} onCommit={(v) => patchSelTransform({ ...selObj.transform, rotation: [v, selObj.transform.rotation[1], selObj.transform.rotation[2]] })} />
                <NumberField label="向" value={selObj.transform.rotation[1]} min={-180} max={180} step={5} onCommit={(v) => patchSelTransform({ ...selObj.transform, rotation: [selObj.transform.rotation[0], v, selObj.transform.rotation[2]] })} />
                <NumberField label="滚" value={selObj.transform.rotation[2]} min={-180} max={180} step={5} onCommit={(v) => patchSelTransform({ ...selObj.transform, rotation: [selObj.transform.rotation[0], selObj.transform.rotation[1], v] })} />
              </div>
              <label className="mb-2 flex items-center gap-2 text-uiSm text-ink-muted">
                缩放
                <input type="range" min={0.2} max={3} step={0.05} value={selObj.uniformScale}
                  onChange={(e) => patchSel({ uniformScale: Number(e.target.value) })}
                  className="slider min-w-0 flex-1" />
                <span className="w-9 shrink-0 text-right font-num">{selObj.uniformScale.toFixed(2)}</span>
              </label>
              <div className="mb-2 flex items-center gap-3 text-uiSm text-ink-secondary">
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={selObj.visible} onChange={(e) => patchSel({ visible: e.target.checked })} />
                  可见
                </label>
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={selObj.locked} onChange={(e) => patchSel({ locked: e.target.checked })} />
                  锁定
                </label>
              </div>
              <p className="mb-1 text-uiSm font-semibold text-ink-muted">标识色</p>
              <div className="mb-2 flex flex-wrap gap-1">
                {TINT_PALETTE.map((c) => (
                  <button key={c} type="button" aria-label={`标识色 ${c}`} aria-pressed={selObj.tint === c}
                    onClick={() => patchSel({ tint: c })}
                    className={`h-5 w-5 rounded-full border transition-transform duration-[120ms] ease-out ${
                      selObj.tint === c ? 'border-2 border-ink-primary scale-110' : 'border-surface-line hover:scale-105'
                    }`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
              <div className="mb-2 flex gap-1.5">
                <button type="button" className={chip} onClick={() => patchSelTransform(mirrorTransform(selObj.transform))}>镜像</button>
                <button type="button" className={chip} onClick={() => patchSelTransform(groundTransform(selObj.transform))}>一键落地</button>
              </div>
              {isCharacterBody(selObj.bodyType) && (
                <>
                  <p className="mb-1 border-t border-surface-line pt-2 text-uiSm font-semibold text-ink-muted">动作模板</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button type="button" className={chip} onClick={() => setPoseLibOpen(true)}>
                      姿势库
                    </button>
                    <button type="button" className={chip} onClick={toRest}>还原 rest</button>
                    <span className="truncate text-uiSm text-ink-muted">
                      {activePoseLabel !== null ? `当前：${activePoseLabel}` : '未套用'}
                    </span>
                  </div>
                  <p className="mb-1 mt-2 flex items-center justify-between border-t border-surface-line pt-2 text-uiSm font-semibold text-ink-muted">
                    骨骼调节
                    <button type="button" className={`${chip} px-2 text-uiSm`} onClick={resetControls}>
                      重置关节
                    </button>
                  </p>
                  {CONTROL_GROUPS.map((g) => (
                    <details key={g.title} open={g.title === '身体'} className="mb-0.5">
                      <summary className="cursor-pointer select-none text-uiSm font-semibold text-ink-secondary hover:text-ink-primary">
                        {g.title}
                      </summary>
                      <div className="mt-0.5 flex flex-col gap-1 pb-1">
                        {g.controls.map((def) => {
                          const val = controlValue(selObj.rig, def.name);
                          return (
                            <label key={def.name} className="flex items-center gap-2 text-uiSm text-ink-muted">
                              <span className="w-14 shrink-0">{def.label}</span>
                              <input
                                type="range"
                                min={def.min}
                                max={def.max}
                                step={1}
                                value={val}
                                onChange={(e) => onControlChange(def.name, Number(e.target.value))}
                                className="slider min-w-0 flex-1"
                              />
                              <span className="w-8 shrink-0 text-right font-num">{val}</span>
                            </label>
                          );
                        })}
                      </div>
                    </details>
                  ))}
                  <p className="mb-1 mt-2 flex items-center justify-between border-t border-surface-line pt-2 text-uiSm font-semibold text-ink-muted">
                    逐骨调节
                    <button type="button" className={`${chip} px-2 text-uiSm`} onClick={clearAllOverrides}>
                      清除单骨覆盖
                    </button>
                  </p>
                  <p className="mb-1 text-uiSm leading-snug text-ink-muted">
                    点骨骼名→视口该骨出旋转环，拖拽微调（含手指）；再点一次取消。
                  </p>
                  {boneGroups().map((g) => (
                    <details key={g.title} className="mb-0.5">
                      <summary className="cursor-pointer select-none text-uiSm font-semibold text-ink-secondary hover:text-ink-primary">
                        {g.title} · {g.bones.length}
                      </summary>
                      <div className="mt-0.5 flex flex-wrap gap-1 pb-1">
                        {g.bones.map((b) => (
                          <button
                            key={b.name}
                            type="button"
                            aria-pressed={selectedBoneName === b.name}
                            disabled={!b.deformable}
                            title={b.deformable ? undefined : '末端骨：不参与蒙皮，旋转无可见效果'}
                            className={`rounded-control border px-1.5 py-0.5 text-uiSm transition-colors duration-[120ms] ease-out ${
                              !b.deformable
                                ? 'cursor-not-allowed border-surface-line text-ink-muted opacity-40'
                                : selectedBoneName === b.name
                                  ? 'border-accent bg-surface-raised text-accent'
                                  : 'border-surface-edge text-ink-secondary hover:bg-surface-raised'
                            }`}
                            onClick={() =>
                              useDirectorStore.getState().selectBone(selectedBoneName === b.name ? null : b.name)
                            }
                          >
                            {b.label}
                          </button>
                        ))}
                      </div>
                    </details>
                  ))}
                </>
              )}
            </div>
          ) : (
            <div className="px-3 py-2.5">
              <p className="mb-2 text-ui font-semibold">场景 · 多维空间</p>
              <button type="button" className={`${chip} w-full text-uiSm ${scene.gridSnap ? chipOn : ''}`}
                aria-pressed={scene.gridSnap}
                onClick={() => useDirectorStore.getState().updateScene({ gridSnap: !scene.gridSnap })}>
                网格吸附 {scene.gridSnap ? '开' : '关'}
              </button>
              <div className="mt-2 flex gap-1">
                <NumberField label="雾" value={scene.fadeEnd} min={30} max={150} step={5}
                  onCommit={(v) => useDirectorStore.getState().updateScene({ fadeEnd: v })} />
              </div>
              <div className="mt-1.5 flex gap-1">
                <NumberField label="地" value={scene.ground.height} min={-5} max={5}
                  onCommit={(v) => useDirectorStore.getState().updateScene({ ground: { ...scene.ground, height: v } })} />
                <NumberField label="透" value={scene.ground.opacity} min={0} max={1} step={0.05}
                  onCommit={(v) => useDirectorStore.getState().updateScene({ ground: { ...scene.ground, opacity: v } })} />
              </div>
              <label className="mt-2 flex items-center gap-1 text-uiSm text-ink-secondary">
                <input type="checkbox" checked={scene.showLabels}
                  onChange={(e) => useDirectorStore.getState().updateScene({ showLabels: e.target.checked })} />
                显示名称标签
              </label>
              <p className="mt-3 text-uiSm text-ink-muted">选中左侧的对象或机位后，这里切换为对应属性表单。</p>
            </div>
          )}

          {/* 变换工具（对象选中时可用） */}
          <div className="border-t border-surface-line px-3 py-2.5">
            <p className="mb-1 text-uiSm font-semibold text-ink-muted">变换工具</p>
            <div className="flex gap-1.5">
              <button type="button" className={modeBtn('translate')} onClick={() => setGizmoMode('translate')} aria-pressed={gizmoMode === 'translate'}>位移 V</button>
              <button type="button" className={modeBtn('rotate')} onClick={() => setGizmoMode('rotate')} aria-pressed={gizmoMode === 'rotate'}>旋转 R</button>
              <button type="button" className={modeBtn('scale')} onClick={() => setGizmoMode('scale')} aria-pressed={gizmoMode === 'scale'}>缩放 F</button>
            </div>
          </div>
        </aside>
      </div>

      <footer className="flex h-11 shrink-0 items-center gap-2 border-t border-surface-line bg-surface-raised px-3 text-uiSm">
        <span className="text-ink-secondary">
          档位「{preset.label}」 · 对象 {objects.length} · 机位 {cameras.length} ·{' '}
          {viewMode === 'camera' ? '相机视角' : '自由视角'} · {report.gpuRenderer ?? 'GPU 未上报'}
        </span>
        <span className="mx-2 h-4 w-px bg-surface-line" />
        <span className="text-ink-muted">左键环绕 · 滚轮缩放 · 中键平移 · 双击机位进视角 · K 建机位 · Ctrl+Z 撤销 / Ctrl+Y 重做 · Esc 取消选择</span>
      </footer>

      <PoseLibraryDialog
        open={poseLibOpen}
        onClose={() => setPoseLibOpen(false)}
        onApply={(p) => void applyPose(p)}
        onRest={toRest}
        activePoseId={activePose}
      />
    </div>
  );
}
